// src/pages/en/WatermarkPage_tw.js
import React, { useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../WatermarkPage.css";

function WatermarkPageTw() {
  const canvasRef = useRef(null);
  const imgObjRef = useRef(null);
  const objectUrlRef = useRef(null);

  // Hidden file input ref
  const fileInputRef = useRef(null);

  // Watermark text input ref
  const inputRef = useRef(null);

  const [darkMode, setDarkMode] = useState(false);

  // CSR canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "浮水印產生器",
      description:
        "將文字浮水印套用到圖片，支援單一或水平/垂直/斜線重複，並可匯出 PNG 的 TooLoves 浮水印產生器。",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "浮水印, 圖片浮水印, 文字浮水印, 斜線浮水印, 重複浮水印, PNG 匯出, TooLoves, 免費",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "TWD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // Sync theme-color with dark/light mode
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // Image/watermark state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [waterText, setWaterText] = useState("");
  const [fontSize, setFontSize] = useState(48);
  const [color, setColor] = useState("#111827");
  const [opacity, setOpacity] = useState(0.2); // 0.05~1
  const [orientation, setOrientation] = useState("horizontal"); // horizontal | vertical | diagonal
  const [repeatX, setRepeatX] = useState(false); // repeat horizontally
  const [repeatY, setRepeatY] = useState(false); // repeat vertically
  const [gap, setGap] = useState(120); // repeat gap (px)
  const [position, setPosition] = useState("center"); // single placement position

  // Trigger hidden file input
  const onPickClick = () => {
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = "";
      } catch {}
      fileInputRef.current.click();
    }
  };

  // Load image on file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    const objUrl = URL.createObjectURL(file);
    objectUrlRef.current = objUrl;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgObjRef.current = img;
      setImageLoaded(true);
      drawCanvas();
      URL.revokeObjectURL(objUrl);
      objectUrlRef.current = null;
    };
    img.src = objUrl;
  };

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleTextKeyDown = (e) => {
    if (e.key === "Enter") {
      requestAnimationFrame(() => autoResizeTextarea());
    }
  };

  const handleTextPaste = () => {
    requestAnimationFrame(() => autoResizeTextarea());
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [waterText]);

  useEffect(() => {
    autoResizeTextarea();
  }, []);

  // Canvas rendering
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const img = imgObjRef.current;
    if (!canvas || !ctx || !img) return;

    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    if (!waterText.trim()) return;

    const fontFamily =
      "Pretendard, Segoe UI, Arial, Apple SD Gothic Neo, Noto Sans KR, sans-serif";
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const lines = waterText.replace(/\r\n?/g, "\n").split("\n");
    const lineHeight = Math.round(fontSize * 1.3);

    let maxLineW = 1;
    for (const line of lines) {
      const w = Math.max(1, ctx.measureText(line).width);
      if (w > maxLineW) maxLineW = w;
    }
    const blockW = maxLineW;
    const blockH = Math.max(1, lines.length * lineHeight);

    const angle =
      orientation === "vertical"
        ? -Math.PI / 2
        : orientation === "diagonal"
        ? -Math.PI / 4
        : 0;

    const cos = Math.cos(Math.abs(angle));
    const sin = Math.sin(Math.abs(angle));
    const rotW = angle !== 0 ? blockW * cos + blockH * sin : blockW;
    const rotH = angle !== 0 ? blockW * sin + blockH * cos : blockH;

    const drawAt = (x, y) => {
      ctx.save();
      ctx.globalAlpha = Math.max(0.01, Math.min(1, opacity));
      ctx.fillStyle = color;
      const mid = (lines.length - 1) / 2;
      if (angle !== 0) {
        ctx.translate(x, y);
        ctx.rotate(angle);
        lines.forEach((line, i) => {
          const yOff = (i - mid) * lineHeight;
          ctx.fillText(line, 0, yOff);
        });
      } else {
        lines.forEach((line, i) => {
          const yOff = (i - mid) * lineHeight;
          ctx.fillText(line, x, y + yOff);
        });
      }
      ctx.restore();
    };

    const stepX = rotW + gap;
    const stepY = rotH + gap;
    if (repeatX || repeatY) {
      const xs =
        repeatX && stepX > 0
          ? Array.from(
              { length: Math.ceil(canvas.width / stepX) + 2 },
              (_, i) => (i - 1) * stepX + stepX / 2
            )
          : [canvas.width / 2];
      const ys =
        repeatY && stepY > 0
          ? Array.from(
              { length: Math.ceil(canvas.height / stepY) + 2 },
              (_, i) => (i - 1) * stepY + stepY / 2
            )
          : [canvas.height / 2];

      for (const y of ys) {
        for (const x of xs) {
          drawAt(x, y);
        }
      }
      return;
    }

    let x = canvas.width / 2;
    let y = canvas.height / 2;
    const pad = 24;
    if (position === "top-left") {
      x = pad + rotW / 2;
      y = pad + rotH / 2;
    } else if (position === "top-right") {
      x = canvas.width - pad - rotW / 2;
      y = pad + rotH / 2;
    } else if (position === "bottom-left") {
      x = pad + rotW / 2;
      y = canvas.height - pad - rotH / 2;
    } else if (position === "bottom-right") {
      x = canvas.width - pad - rotW / 2;
      y = canvas.height - pad - rotH / 2;
    }
    drawAt(x, y);
  };

  useEffect(() => {
    drawCanvas();
  }, [
    imageLoaded,
    waterText,
    fontSize,
    color,
    opacity,
    orientation,
    repeatX,
    repeatY,
    gap,
    position,
  ]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = "watermark.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReset = () => {
    setWaterText("");
    setFontSize(48);
    setColor("#111827");
    setOpacity(0.2);
    setOrientation("horizontal");
    setRepeatX(false);
    setRepeatY(false);
    setGap(120);
    setPosition("center");
    setImageLoaded(false);
    imgObjRef.current = null;

    requestAnimationFrame(() => autoResizeTextarea());
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "zh-Hant" }}>
        <title>浮水印產生器 | TooLoves</title>
        <meta
          name="description"
          content="上傳圖片並輸入文字，即可將半透明浮水印以單一或水平/垂直/斜線重複方式套用，並可匯出 PNG。"
        />
        <meta
          name="keywords"
          content="浮水印, 圖片浮水印, 文字浮水印, 斜線浮水印, 重複浮水印, PNG 匯出, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="zh_TW" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="浮水印產生器 | TooLoves" />
        <meta
          property="og:description"
          content="將浮水印輕鬆套用到圖片並匯出 PNG。"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-watermark.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="浮水印產生器 | TooLoves" />
        <meta
          name="twitter:description"
          content="將浮水印輕鬆套用到圖片並匯出 PNG。"
        />
        <meta name="twitter:image" content="/og-watermark.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* App bar */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/tw" className="brand-link" aria-label="前往 TooLoves 首頁">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "淺色模式" : "深色模式"}
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container">
        <section className="generator-card">
          <h2 className="card-title">圖片浮水印</h2>
          <p className="card-subtitle">
            上傳圖片並輸入文字，即可將半透明浮水印以單一或水平/垂直/斜線重複方式套用，
            並可匯出 PNG。
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* Image upload (hidden input + button) */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="wm-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="選擇圖片檔案"
                className="btn btn-primary"
              >
                選擇檔案
              </button>
            </div>

            {/* Watermark text */}
            <div className="input-row" style={{ marginTop: 8 }}>
              <textarea
                ref={inputRef}
                className="text-input text-inputtextarea"
                placeholder="請輸入浮水印文字"
                value={waterText}
                onChange={(e) => {
                  setWaterText(e.target.value);
                  autoResizeTextarea();
                }}
                onKeyDown={handleTextKeyDown}
                onPaste={handleTextPaste}
                rows={1}
              />
            </div>

            {/* Options */}
            <div className="options-row">
              <div className="field">
                <label className="form-label">字體大小</label>
                <input
                  type="range"
                  min="16"
                  max="160"
                  step="2"
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                />
                <div className="hint">{fontSize}px</div>
              </div>

              <div className="field">
                <label className="form-label">文字顏色</label>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
              </div>

              <div className="field">
                <label className="form-label">不透明度</label>
                <input
                  type="range"
                  min="0.05"
                  max="1"
                  step="0.05"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                />
                <div className="hint">{Math.round(opacity * 100)}%</div>
              </div>

              <div className="field">
                <label className="form-label">文字方向</label>
                <select value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                  <option value="horizontal">水平</option>
                  <option value="vertical">垂直</option>
                  <option value="diagonal">斜線</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">水平重複</label>
                <input
                  type="checkbox"
                  checked={repeatX}
                  onChange={(e) => setRepeatX(e.target.checked)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">垂直重複</label>
                <input
                  type="checkbox"
                  checked={repeatY}
                  onChange={(e) => setRepeatY(e.target.checked)}
                />
              </div>

              <div className="field">
                <label className="form-label">重複間距</label>
                <input
                  type="range"
                  min="16"
                  max="400"
                  step="4"
                  value={gap}
                  onChange={(e) => setGap(Number(e.target.value))}
                  disabled={!(repeatX || repeatY)}
                />
                <div className="hint">{gap}px</div>
              </div>

              <div className="field">
                <label className="form-label">單一放置位置</label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  disabled={repeatX || repeatY}
                >
                  <option value="center">置中</option>
                  <option value="top-left">左上</option>
                  <option value="top-right">右上</option>
                  <option value="bottom-left">左下</option>
                  <option value="bottom-right">右下</option>
                </select>
              </div>
            </div>
          </form>

          {/* Preview */}
          <div className="preview-card">
            {imageLoaded ? (
              <>
                <div className="canvas-wrap">
                  <canvas ref={canvasRef} id="wm-canvas" />
                </div>
                <div className="actions">
                  <button onClick={handleDownload}>下載 PNG</button>
                  <button className="btn-secondary" onClick={handleReset}>
                    重設
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">請選擇圖片並輸入浮水印文字。</p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法律資訊連結">
          <Link to="/tw/terms" className="footer-link">服務條款</Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to="/tw/privacy" className="footer-link">隱私權政策</Link>
        </nav>
      </footer>
    </div>
  );
}

export default WatermarkPageTw;